import { Injectable, InjectionToken, Signal, effect, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AppActivityService } from './app-activity.service';
import { ConnectivityService } from './connectivity.service';

/** Reload seam — injectable so tests observe the reload without navigating jsdom. */
export const RELOAD = new InjectionToken<() => void>('RELOAD', {
  providedIn: 'root',
  factory: () => () => location.reload(),
});

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private sw = inject(SwUpdate);
  private activity = inject(AppActivityService);
  private connectivity = inject(ConnectivityService);
  private toastr = inject(ToastrService);
  private reload = inject(RELOAD);

  private static readonly DEBOUNCE_MS = 3_000;
  private static readonly CHECK_INTERVAL_MS = 30 * 60_000;

  private readonly _updateReady = signal(false);
  /** True once a new version is downloaded and ready to activate. Drives the update banner. */
  readonly updateReady: Signal<boolean> = this._updateReady.asReadonly();

  private debounce: ReturnType<typeof setTimeout> | null = null;
  private reloading = false;

  constructor() {
    if (!this.sw.isEnabled) return; // dev / no-SW: stay inert

    this.sw.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this._updateReady.set(true));

    // Auto-apply once idle. The debounce re-checks busy at fire time so a reload can't land
    // in the middle of a tap; a mid-debounce busy flip cancels and reschedules on the next idle.
    effect(() => {
      const ready = this._updateReady();
      const busy = this.activity.busy();
      this.clearDebounce();
      if (ready && !busy) {
        this.debounce = setTimeout(() => {
          this.debounce = null;
          if (this._updateReady() && !this.activity.busy()) void this.apply();
        }, PwaUpdateService.DEBOUNCE_MS);
      }
    });

    if (typeof window !== 'undefined') {
      setInterval(() => this.checkForUpdate(), PwaUpdateService.CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.checkForUpdate();
      });
    }
  }

  /** Manual "Apply now" and the auto-apply target: activate the waiting SW and reload once. */
  async apply(): Promise<void> {
    if (this.reloading) return;
    this.reloading = true;
    this.toastr.info('Updating to the latest version…');
    try {
      await this.sw.activateUpdate();
      this.reload();
    } catch {
      // Activation failed (network/SW hiccup) — allow a later retry rather than locking out
      // updates for the rest of the session. The update banner stays visible for another attempt.
      this.reloading = false;
    }
  }

  private checkForUpdate(): void {
    if (!this.sw.isEnabled || !this.connectivity.online()) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    void this.sw.checkForUpdate();
  }

  private clearDebounce(): void {
    if (this.debounce) { clearTimeout(this.debounce); this.debounce = null; }
  }
}
